import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { Wrench, Plus, X, CheckCircle, Loader2, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface LaborItem  { desc: string; hours: string; rate: string; total: string }
interface PartsItem  { part_no: string; desc: string; qty: string; price: string; total: string }

interface RepairOrder {
    ro_id: number;
    shop_id: number;
    vehicle_vin: string | null;
    vehicle_desc: string | null;
    status: string;
    labor_items: LaborItem[] | null;
    parts_items: PartsItem[] | null;
    labor_total: string | null;
    parts_total: string | null;
    tax_amount: string | null;
    total_amount: string | null;
    ro_notes: string | null;
    payment_mode: string | null;
}

const TAX_RATE = 0.0825;

const STATUS_COLOR: Record<string, string> = {
    OPEN:        "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-orange-100 text-orange-700",
    INVOICED:    "bg-violet-100 text-violet-700",
    PAID:        "bg-green-100 text-green-700",
};

export default function RepairOrderDetailPage() {
    const { roId } = useParams();
    const navigate = useNavigate();
    const [ro,       setRo]       = useState<RepairOrder | null>(null);
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [laborItems, setLaborItems] = useState<LaborItem[]>([]);
    const [partsItems, setPartsItems] = useState<PartsItem[]>([]);
    const [notes,      setNotes]      = useState("");

    useEffect(() => {
        axios.get(`${API_URL}/repair-shops/staff/repair-orders/${roId}`, { withCredentials: true })
            .then((r) => {
                const data: RepairOrder = r.data.repair_order;
                setRo(data);
                setLaborItems(data.labor_items ?? []);
                setPartsItems(data.parts_items ?? []);
                setNotes(data.ro_notes ?? "");
            })
            .catch(() => setRo(null))
            .finally(() => setLoading(false));
    }, [roId]);

    const calcTotal = () => {
        const labor = laborItems.reduce((s, i) => s + parseFloat(i.total || "0"), 0);
        const parts = partsItems.reduce((s, i) => s + parseFloat(i.total || "0"), 0);
        const tax   = (labor + parts) * TAX_RATE;
        return { labor, parts, tax, total: labor + parts + tax };
    };

    const save = async () => {
        if (!ro) return;
        setSaving(true);
        const totals = calcTotal();
        try {
            const res = await axios.patch(
                `${API_URL}/repair-shops/staff/repair-orders/${ro.ro_id}`,
                {
                    labor_items: laborItems,
                    parts_items: partsItems,
                    labor_total: totals.labor.toFixed(2),
                    parts_total: totals.parts.toFixed(2),
                    tax_amount:  totals.tax.toFixed(2),
                    total_amount: totals.total.toFixed(2),
                    ro_notes: notes,
                    status: "IN_PROGRESS",
                },
                { withCredentials: true }
            );
            setRo(res.data.repair_order);
        } finally {
            setSaving(false);
        }
    };

    const closeRO = async () => {
        if (!ro) return;
        await save();
        const res = await axios.patch(
            `${API_URL}/repair-shops/staff/repair-orders/${ro.ro_id}/close`,
            {},
            { withCredentials: true }
        );
        setRo(res.data.repair_order);
    };

    const addLabor = () => setLaborItems((l) => [...l, { desc: "", hours: "", rate: "", total: "" }]);
    const addPart  = () => setPartsItems((p) => [...p, { part_no: "", desc: "", qty: "", price: "", total: "" }]);

    const updateLabor = (i: number, field: keyof LaborItem, val: string) =>
        setLaborItems((l) => l.map((x, j) => {
            if (j !== i) return x;
            const updated = { ...x, [field]: val };
            updated.total = (parseFloat(field === "hours" ? val : updated.hours || "0") *
                             parseFloat(field === "rate"  ? val : updated.rate  || "0")).toFixed(2);
            return updated;
        }));

    const updatePart = (i: number, field: keyof PartsItem, val: string) =>
        setPartsItems((p) => p.map((x, j) => {
            if (j !== i) return x;
            const updated = { ...x, [field]: val };
            updated.total = (parseFloat(field === "qty"   ? val : updated.qty   || "0") *
                             parseFloat(field === "price" ? val : updated.price || "0")).toFixed(2);
            return updated;
        }));

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
    if (!ro)     return <p className="p-8 text-center text-muted-foreground">Repair order not found.</p>;

    const totals   = calcTotal();
    const editable = ro.status !== "INVOICED" && ro.status !== "PAID";

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground transition-colors">
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold flex items-center gap-2">
                            <Wrench className="h-5 w-5 text-indigo-600" />
                            Repair Order #{ro.ro_id}
                        </h1>
                        {ro.vehicle_desc && <p className="text-sm text-muted-foreground mt-0.5">{ro.vehicle_desc}</p>}
                        {ro.vehicle_vin  && <p className="text-xs text-muted-foreground font-mono">{ro.vehicle_vin}</p>}
                    </div>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLOR[ro.status] ?? "bg-gray-100 text-gray-600"}`}>
                    {ro.status.replace("_", " ")}
                </span>
            </div>

            {/* Labor */}
            <Card className="p-5 space-y-3">
                <h2 className="font-semibold">Labor</h2>
                {laborItems.map((item, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 items-center">
                        <Input className="col-span-2" placeholder="Description" value={item.desc}
                            disabled={!editable}
                            onChange={(e) => updateLabor(i, "desc", e.target.value)} />
                        <Input type="number" placeholder="Hours" value={item.hours}
                            disabled={!editable}
                            onChange={(e) => updateLabor(i, "hours", e.target.value)} />
                        <Input type="number" placeholder="Rate/hr" value={item.rate}
                            disabled={!editable}
                            onChange={(e) => updateLabor(i, "rate", e.target.value)} />
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-mono">${item.total || "0.00"}</span>
                            {editable && (
                                <button onClick={() => setLaborItems((l) => l.filter((_, j) => j !== i))}>
                                    <X className="h-4 w-4 text-red-400 hover:text-red-600" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {editable && (
                    <Button variant="outline" size="sm" onClick={addLabor}>
                        <Plus className="h-3 w-3 mr-1" /> Add Labor
                    </Button>
                )}
            </Card>

            {/* Parts */}
            <Card className="p-5 space-y-3">
                <h2 className="font-semibold">Parts</h2>
                {partsItems.map((item, i) => (
                    <div key={i} className="grid grid-cols-6 gap-2 items-center">
                        <Input placeholder="Part #" value={item.part_no}
                            disabled={!editable}
                            onChange={(e) => updatePart(i, "part_no", e.target.value)} />
                        <Input className="col-span-2" placeholder="Description" value={item.desc}
                            disabled={!editable}
                            onChange={(e) => updatePart(i, "desc", e.target.value)} />
                        <Input type="number" placeholder="Qty" value={item.qty}
                            disabled={!editable}
                            onChange={(e) => updatePart(i, "qty", e.target.value)} />
                        <Input type="number" placeholder="Price" value={item.price}
                            disabled={!editable}
                            onChange={(e) => updatePart(i, "price", e.target.value)} />
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-mono">${item.total || "0.00"}</span>
                            {editable && (
                                <button onClick={() => setPartsItems((p) => p.filter((_, j) => j !== i))}>
                                    <X className="h-4 w-4 text-red-400 hover:text-red-600" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {editable && (
                    <Button variant="outline" size="sm" onClick={addPart}>
                        <Plus className="h-3 w-3 mr-1" /> Add Part
                    </Button>
                )}
            </Card>

            {/* Notes */}
            <Card className="p-5">
                <h2 className="font-semibold mb-2">Technician Notes</h2>
                <textarea
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-background resize-none"
                    rows={3}
                    value={notes}
                    disabled={!editable}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Work performed, findings, recommendations…"
                />
            </Card>

            {/* Totals */}
            <Card className="p-5 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Labor</span><span>${totals.labor.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Parts</span><span>${totals.parts.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax ({(TAX_RATE * 100).toFixed(2)}%)</span><span>${totals.tax.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold text-base border-t pt-2 mt-2">
                    <span>Total</span><span>${totals.total.toFixed(2)}</span>
                </div>
            </Card>

            {/* Actions */}
            {editable ? (
                <div className="flex gap-3">
                    <Button variant="outline" onClick={save} disabled={saving}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Save Changes
                    </Button>
                    <Button onClick={closeRO} disabled={saving} className="bg-green-600 text-white hover:bg-green-700">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Close & Generate Invoice
                    </Button>
                </div>
            ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-800 text-sm font-medium">
                    {ro.status === "PAID"
                        ? "This repair order has been paid."
                        : "Invoice generated — customer notified to pay online."}
                </div>
            )}
        </div>
    );
}
